import {
  useState,
  useLayoutEffect,
  useRef,
  type RefObject,
  type CSSProperties,
} from "react";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";

const DEFAULT_HEADER_HEIGHT = 80;
const DEFAULT_TABLE_HEIGHT = 600;
const MIN_TABLE_HEIGHT = 520;
const FALLBACK_PAGINATION_HEIGHT = 0;
const DEFAULT_Y_PADDING = 20;
const BREADCRUMB_HEIGHT = 30;

interface usePillarTableDynamicHeightReturn {
  tableHeight: number;
  headerAboveTableRef: RefObject<HTMLDivElement>;
  tableScrollWrapperStyle: CSSProperties;
  tableScrollWrapperClasses: string;
}

export const usePillarTableDynamicHeight =
  (): usePillarTableDynamicHeightReturn => {
    const headerAboveTableRef = useRef<HTMLDivElement>(null);

    const { isDynamicHeight, tablePaginationRef } = usePillarTableContext();

    const [tableHeight, setTableHeight] =
      useState<number>(DEFAULT_TABLE_HEIGHT);

    const tableScrollWrapperStyle: CSSProperties = isDynamicHeight
      ? { maxHeight: `${tableHeight}px` }
      : {};

    const tableScrollWrapperClasses = "overflow-auto w-full";

    useLayoutEffect(() => {
      if (!isDynamicHeight) {
        return;
      }

      const calculateHeight = () => {
        if (!headerAboveTableRef.current) {
          return;
        }

        const filterRect = headerAboveTableRef.current;

        const paginationHeight =
          tablePaginationRef?.current?.offsetHeight ??
          FALLBACK_PAGINATION_HEIGHT;

        const availableHeight =
          window.innerHeight -
          DEFAULT_HEADER_HEIGHT -
          BREADCRUMB_HEIGHT -
          filterRect.offsetHeight -
          paginationHeight -
          DEFAULT_Y_PADDING * 2;

        const finalTableHeight = Math.max(availableHeight, MIN_TABLE_HEIGHT);

        setTableHeight(finalTableHeight);
      };

      calculateHeight();

      window.addEventListener("resize", calculateHeight);

      const resizeObserver = new ResizeObserver(() => {
        calculateHeight();
      });

      if (headerAboveTableRef.current) {
        resizeObserver.observe(headerAboveTableRef.current);
      }

      return () => {
        window.removeEventListener("resize", calculateHeight);
        resizeObserver.disconnect();
      };
    }, [isDynamicHeight]);

    return {
      tableHeight,
      headerAboveTableRef,
      tableScrollWrapperStyle,
      tableScrollWrapperClasses,
    };
  };
